import { useModals } from '@/components/modals/ModalProvider';
import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import rule from '@/libs/antd/rules';
import { MasterData } from '@/libs/fleet-api/services/master-data';
import {
	AgreementPrintDetail,
	SubmitAgreementPrintingPayload,
} from '@/libs/fleet-api/services/print';
import { Col, DatePicker, Divider, Form, Input, Row, Select } from 'antd';
import dayjs from 'dayjs';
import { FC } from 'react';

export const useCovenantModal = () =>
	useAsyncModal<
		{
			record: SubmitAgreementPrintingPayload['covenants'][number];
			detail?: AgreementPrintDetail['covenants'][number];
		},
		SubmitAgreementPrintingPayload['covenants'][number]
	>();

export type CovenantModalProps = ReturnType<typeof useCovenantModal> & {
	covenantStatusOptions: MasterData[];
};

const CovenantModal: FC<CovenantModalProps> = ({
	modal,
	close,
	data,
	covenantStatusOptions,
}) => {
	const { openPreviewModal } = useModals();
	const { form, fieldName } =
		useForm<SubmitAgreementPrintingPayload['covenants'][number]>();

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="CANCEL"
			okText="SAVE"
			onCancel={() => close()}
			onOk={form.submit}
			afterOpenChange={(isOpen) => {
				if (isOpen && data) {
					form.setFieldsValue(data.record);
				} else {
					form.resetFields();
				}
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<div style={{ marginBottom: 20 }}>
					<FleetTitle>Edit Data Covenant</FleetTitle>
				</div>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onFinish={close}
				>
					<Form.Item
						name={fieldName('id')}
						noStyle
					/>
					<Form.Item
						label="Covenant"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						{data?.detail?.covenant ?? '-'}
					</Form.Item>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Kategori">
								{data?.detail?.category?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Priority">
								{data?.detail?.priority?.value ?? '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Repeat">
								{data?.detail?.repeatFrequency?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Jenis Monitoring">
								{data?.detail?.monitoringType?.value ?? '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item label="Analisa Lebih Lanjut">
								{data?.detail?.furtherAnalysis?.value ?? '-'}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label="Tanggal Upload Dokumen">
								{data?.detail?.uploadDocumentDate
									? dayjs(data.detail.uploadDocumentDate).format('DD/MM/YYYY')
									: '-'}
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={20}>
						<Col span={12}>
							<Form.Item label="View Dokumen">
								{data?.detail?.covenantDocument ? (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(data.detail!.covenantDocument);
										}}
									>
										{data.detail.covenantDocument.name}
									</a>
								) : (
									'-'
								)}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name={fieldName('statusId')}
								label="Status"
								rules={[rule.required()]}
							>
								<Select
									options={covenantStatusOptions.map((option) => ({
										label: option.value,
										value: option.id,
									}))}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Row gutter={40}>
						<Col span={12}>
							<Form.Item
								name={fieldName('dueDate')}
								label="Due Date"
								getValueProps={(value) => ({
									value: value && dayjs(value),
								})}
								normalize={(value) =>
									value && dayjs(value).utc(true).toISOString()
								}
							>
								<DatePicker
									disabled
									disabledDate={(current) =>
										current && current < dayjs().startOf('day')
									}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Form.Item
						label="Notes FRM"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						<Input.TextArea
							rows={4}
							disabled
							value={data?.detail?.notes}
						/>
					</Form.Item>
					<Divider />
					<Form.Item
						name={fieldName('notes')}
						rules={[rule.required()]}
						label="Keterangan"
						labelCol={{ span: 6 }}
						wrapperCol={{ span: 18 }}
					>
						<Input.TextArea rows={4} />
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default CovenantModal;
